'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Host adapter contract for commerce-bound blocks — product grids, product
 * detail, facet panels, kit configurators and enquiry CTAs.
 *
 * <p>Hosts implement this against the Sorsy storefront read API
 * (`/public/storefront/catalogue/**`) and mount {@link CommerceSourceProvider}
 * once near the root. Follows {@link ./blogSource} and {@link ./learningSource}
 * exactly: when no provider is mounted {@link useCommerceSource} returns
 * `null`, and a consuming block renders an inline placeholder rather than
 * throwing — "backend not wired yet" is a normal state, not an error.
 *
 * <h3>Two rules the shapes below encode</h3>
 *
 * <p><strong>Money is a string, never a number.</strong> `MoneyValue.amount`
 * arrives as a decimal string and stays one. Parsing it to a JS number to
 * format it reintroduces binary floating point on a value the backend went to
 * some trouble to keep exact — £0.1 + £0.2 is not £0.3, and a catalogue that
 * is a penny out on a £4,000 kit is a catalogue nobody trusts. Currency
 * travels with the amount because this platform runs in more than one.
 *
 * <p><strong>No method takes a tenant id.</strong> A storefront visitor is
 * anonymous and the site is whichever hostname they typed; the backend
 * resolves tenant and channel from the request host. A tenant id crossing this
 * interface would mean the browser could name someone else's tenant, which is
 * a tenant-isolation hole rather than a convenience. The optional `domain`
 * belongs to the host adapter's own configuration, not to a block's call.
 */

/** A decimal amount with its currency, exactly as the API sent it. */
export interface MoneyValue {
  /** Decimal string — scale 2 for totals, 4 for unit prices. Never a number. */
  amount: string;
  /** ISO 4217, e.g. `GBP`, `TRY`. Never defaulted by a block. */
  currency: string;
  /** Whether `amount` already includes tax, which differs by channel. */
  taxInclusive: boolean;
}

export interface CommerceImage {
  url: string;
  /** Empty string means decorative — render `<div>`, not `<img alt="">`. */
  alt: string;
  role?: string;
  position?: number;
}

/** Coarse stock state. Deliberately not a quantity: exposing "3 left" on a
 *  public page is a business decision, not a rendering one. */
export type CommerceAvailability =
  | 'IN_STOCK'
  | 'LOW_STOCK'
  | 'BACKORDER'
  | 'OUT_OF_STOCK'
  | 'PREORDER';

/** One product as a grid or carousel renders it. */
export interface ProductCard {
  id: number;
  slug: string;
  /** The canonical public path. Blocks link to this and never build a URL
   *  from the slug themselves — a kit lives at `/kits/{slug}` while a plain
   *  product lives at `/p/{slug}`, and only the backend knows which. */
  publicPath: string;
  title: string;
  brandName?: string;
  brandSlug?: string;
  heroImage?: CommerceImage;
  price?: MoneyValue;
  /** Was-price, when the channel has one. Absent means "no discount", which
   *  is not the same as equal to `price`. */
  listPrice?: MoneyValue;
  availability?: CommerceAvailability;
  productType?: string;
}

export interface ProductDetail extends ProductCard {
  shortDescription?: string;
  /**
   * Plain text, not markup. Render it as text — `white-space: pre-wrap` keeps
   * the author's line breaks. There is deliberately no `descriptionHtml`
   * sibling yet (contrast {@link ./blogSource}'s `body`/`bodyHtml` split): the
   * catalogue backend stores no markup today, and adding the field before it
   * does would invite `dangerouslySetInnerHTML` on a value nothing sanitises.
   */
  description?: string;
  sku?: string;
  gtin?: string;
  mpn?: string;
  images?: CommerceImage[];
  attributes?: ProductAttribute[];
  documents?: ProductDocument[];
  /** Set when this slug has moved. The route must issue the redirect rather
   *  than render — two URLs serving one product is the duplicate-content
   *  failure the storefront exists to avoid. */
  redirectTo?: string;
  redirectStatus?: number;
}

export interface ProductAttribute {
  code: string;
  label: string;
  value: string;
  group?: string;
}

export interface ProductDocument {
  url: string;
  title: string;
  kind?: string;
}

export interface CategoryNode {
  id: number;
  name: string;
  slug: string;
  /** Full slash-joined path from the root — what `/c/{path}` resolves. */
  path: string;
  featured?: boolean;
  children?: CategoryNode[];
}

export interface BrandCard {
  id: number;
  name: string;
  slug: string;
  logoUrl?: string;
  productCount?: number;
}

export interface FacetValue {
  value: string;
  label: string;
  count?: number;
  selected?: boolean;
}

export interface Facet {
  code: string;
  label: string;
  /** e.g. `checkbox`, `range`. The panel picks its control from this. */
  type: string;
  values: FacetValue[];
}

/** Query for {@link CommerceSource.listProducts}. Every field optional: a
 *  bare call is "the default listing for this channel". */
export interface ProductListingQuery {
  categorySlugPath?: string;
  brandSlug?: string;
  /** Facet code to selected values. */
  attributes?: Record<string, string[]>;
  q?: string;
  sort?: string;
  /**
   * **Zero-based.** `0` is the first page; the backend uses `page * size` as
   * its offset. A URL is conventionally 1-based (`?page=1` is the first page),
   * so a host reading one must subtract before calling — leaving this
   * undocumented already cost one off-by-one where `?page=1` skipped the first
   * `size` products and made the real first page unreachable.
   */
  page?: number;
  size?: number;
  inStockOnly?: boolean;
  /** Decimal strings, for the same reason {@link MoneyValue.amount} is. */
  minPrice?: string;
  maxPrice?: string;
  featured?: boolean;
  productType?: string;
}

export interface ProductListing {
  items: ProductCard[];
  total: number;
  /** Zero-based, echoing {@link ProductListingQuery.page}. */
  page: number;
  size: number;
  facets: Facet[];
  /** A search that resolves to exactly one canonical destination. */
  redirectTo?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cart, checkout and orders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A decimal as the API sent it — money or quantity.
 *
 * <p>The same rule as {@link MoneyValue.amount} and for the same reason, but without the currency
 * repeated on every figure: a cart states its currency once, and a quantity has none. A block
 * formats these through the shared money formatter, which works in integer space; nothing here is
 * ever handed to `Number()`.
 */
export type DecimalString = string;

export interface CartLine {
  id: number;
  parentLineId?: number | null;
  lineType?: string;
  productId?: number | null;
  description: string;
  quantity: DecimalString;
  unitPrice: DecimalString;
  lineTotal: DecimalString;
  taxRate?: DecimalString;
  taxAmount?: DecimalString;
  taxInclusive: boolean;
  /** What the shopper may still order. Absent when the tenant does not track it. */
  availableQuantity?: DecimalString | null;
  availabilityNote?: string | null;
}

export interface ShippingOption {
  /** Pass back as `shippingMethodId` at checkout. */
  id: number;
  carrier?: string;
  serviceName?: string;
  transitDays?: number;
  /** e.g. `15:00` — the cut-off for same-day despatch. */
  orderByTime?: string;
  price: DecimalString;
  taxAmount?: DecimalString;
}

export interface Cart {
  /** The bearer of the basket. A guest has no account, so this token IS the
   *  identity of their basket — treat it as a credential, not an id. */
  token: string;
  currency: string;
  status: string;
  lines: CartLine[];
  /** Empty until the tenant has shipping configured, or when nothing can carry the basket. */
  shippingOptions: ShippingOption[];
  subtotal: DecimalString;
  discountTotal: DecimalString;
  shippingTotal: DecimalString;
  taxTotal: DecimalString;
  total: DecimalString;
}

export interface CartAddition {
  productId: number;
  /** Decimal string: this platform sells by weight and length, not only by the each. */
  quantity: DecimalString;
  note?: string;
}

export interface CheckoutAddress {
  recipientName?: string;
  company?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  locality?: string;
  region?: string;
  postcode?: string;
  /** ISO 3166-1 alpha-2. */
  countryCode?: string;
  phone?: string;
}

/** How a basket is to reach the shopper — see {@link CommerceSource.setCartFulfilment}. */
export interface CartFulfilment {
  /** `DELIVERY` or `COLLECTION`. Left undefined, the basket keeps what it has. */
  fulfilmentType?: string;
  /** One of {@link Cart.shippingOptions}' ids. */
  shippingMethodId?: number;
  /** Set together with the method when the charge depends on where it is going. */
  deliveryAddress?: CheckoutAddress;
  collectionLocationId?: number;
}

export interface CheckoutRequest {
  cartToken: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  /** `DELIVERY` or `COLLECTION`. */
  fulfilmentType?: string;
  /** One of {@link Cart.shippingOptions}' ids. Required for delivery. */
  shippingMethodId?: number;
  deliveryAddress?: CheckoutAddress;
  /** Required with `COLLECTION`: one of {@link CommerceSource.collectionPoints}' ids. */
  collectionLocationId?: number;
  /**
   * The shopper's own address when nothing is being delivered. Its postcode is then the proof
   * that finds the order again, where a delivery's postcode would be.
   */
  billingAddress?: CheckoutAddress;
  customerPoNumber?: string;
  note?: string;
}

/** A place this shop offers click & collect from. */
export interface CollectionPoint {
  id: number;
  name: string;
  /** What a collecting shopper needs to know — where the counter is, what to bring. */
  note?: string | null;
  address: {
    line1: string;
    line2?: string | null;
    line3?: string | null;
    locality?: string | null;
    region?: string | null;
    postcode?: string | null;
    countryCode: string;
    phone?: string | null;
    /** Decimal strings. */
    latitude?: string | null;
    longitude?: string | null;
  };
  /** IANA zone the hours are local to. */
  timeZone: string;
  /** Weekly hours: ISO weekday (1 = Monday), local `HH:mm`. A weekday with no row is closed. */
  hours: { weekday: number; opensAt: string; closesAt: string }[];
  /** Upcoming dates the weekly hours would call open but are not; ISO dates. */
  closures: { date: string; reason?: string | null }[];
}

export interface OrderLine {
  id: number;
  sku?: string;
  description: string;
  brand?: string | null;
  quantity: DecimalString;
  unitPrice: DecimalString;
  lineTotal: DecimalString;
  taxAmount?: DecimalString;
  taxInclusive: boolean;
  status?: string;
  quantityShipped?: DecimalString;
  /** How much of this line the shopper may still ask to return; absent or `"0.000"` when none. */
  quantityReturnable?: DecimalString | null;
}

/** Why a shopper is sending something back — the reasons a shopper can know. */
export type ReturnReason = 'faulty' | 'damaged' | 'wrong_item' | 'not_required';

export interface OrderReturnLine {
  orderLineId?: number | null;
  description?: string | null;
  sku?: string | null;
  quantity: DecimalString;
}

/** A return as its shopper sees it. Instants are ISO-8601 strings. */
export interface OrderReturn {
  rmaNumber: string;
  orderNumber?: string | null;
  /** `PENDING`, `APPROVED`, `PROCESSING`, `COMPLETED`, `REJECTED` … */
  status: string;
  reason?: string | null;
  /** The shopper's own words, followed by the shop's reason if it was turned down. */
  reasonDetail?: string | null;
  /** Once settled: `REFUND`, `PARTIAL_REFUND`, `REPLACE`, `REPAIR`, `CREDIT`, `REJECTED`. */
  resolution?: string | null;
  requestedAt?: string | null;
  receivedAt?: string | null;
  resolvedAt?: string | null;
  /** Set once approved: how and where to send the items. */
  returnCarrier?: string | null;
  returnTrackingNumber?: string | null;
  returnToName?: string | null;
  returnToAddressText?: string | null;
  lines: OrderReturnLine[];
}

export interface ReturnRequestInput {
  orderNumber: string;
  reason: ReturnReason;
  reasonDetail?: string;
  contactPhone?: string;
  lines: { orderLineId: number; quantity: DecimalString }[];
  /** A guest's proof of the order — the same two facts that track it. Omit when signed in. */
  proof?: OrderLookup;
}

export interface Order {
  id: number;
  orderNumber: string;
  status: string;
  fulfilmentStatus?: string;
  paymentStatus?: string;
  currency: string;
  deliveryName?: string;
  deliveryAddressText?: string;
  deliveryPostcode?: string;
  deliveryCountryCode?: string;
  shippingServiceName?: string;
  shippingCarrier?: string;
  shippingTransitDays?: number;
  subtotal: DecimalString;
  discountTotal: DecimalString;
  shippingTotal: DecimalString;
  taxTotal: DecimalString;
  total: DecimalString;
  lines: OrderLine[];
  /** `DELIVERY`, `COLLECTION` … */
  fulfilmentType?: string | null;
  collectionPointName?: string | null;
  collectionAddressText?: string | null;
  /** Once the order is on the collection shelf: the code the counter will ask for, and the deadline. */
  collectionCode?: string | null;
  /** ISO-8601 instant. */
  collectBy?: string | null;
  /** Every return raised against the order, newest first. */
  returns?: OrderReturn[];
}

/** What a shopper must show to pay for an order — see {@link CommerceSource.payForOrder}. */
export interface PaymentRequest {
  orderNumber: string;
  contactEmail: string;
  deliveryPostcode: string;
  /** Where the acquirer returns the shopper. A path on this site; anything else is dropped
   *  server-side, because an open redirect on a payment page is how phishing borrows trust. */
  returnUrl?: string;
  /** An opaque handle from the gateway's OWN client library. Card numbers never
   *  reach this API — that boundary is what keeps the platform out of scope for
   *  card-data compliance. Absent for a hosted-page gateway. */
  instrumentToken?: string;
}

export interface PaymentResult {
  orderNumber: string;
  /** `APPROVED`, `PENDING`, `FAILED` or `ALREADY_PAID`. */
  status: string;
  amount: DecimalString;
  currency: string;
  /** Send the shopper here — 3-D Secure, or a hosted page. Absent when the gateway settled it
   *  inline, which is the only case where the status alone is the whole answer. */
  actionUrl?: string;
  /** The gateway's own wording, written for a cardholder. Safe to show. */
  message?: string;
}

/** One outcome a simulated gateway can be asked for. Never present on a live acquirer. */
export interface TestInstrument {
  token: string;
  label: string;
}

/** How a shop takes money, as much as a browser is entitled to know. */
export interface PaymentOptions {
  /** The acquirer's key, e.g. `stripe`, `sandbox`. */
  provider: string;
  /** True when payments move no money. The page MUST say so — a shop that looks
   *  like it is taking a card and is not is the one failure a shopper cannot
   *  detect and would not forgive. */
  testMode: boolean;
  /** False leaves checkout working and the order unpaid. */
  enabled: boolean;
  /** Empty for every real acquirer; populated by a simulator so a tester can
   *  choose an outcome without the block knowing any gateway's magic values. */
  testInstruments: TestInstrument[];
}

/** Both proofs are required together — see {@link CommerceSource.trackOrder}. */
export interface OrderLookup {
  contactEmail: string;
  deliveryPostcode: string;
}

// ── Account ───────────────────────────────────────────────────────────────

/** The signed-in shopper, as much of them as a page needs. */
export interface AccountProfile {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

/** A new account. No password yet: it is chosen with the code emailed to `email`. */
export interface RegisterRequest {
  email: string;
  firstName: string;
  lastName: string;
}

/** A new account's address confirmed with the emailed code, and its password chosen. */
export interface AccountActivationRequest {
  email: string;
  /** From the email, or the link's `?token=`. */
  code: string;
  password: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

/**
 * A new password, proved with the code the forgotten-password email sent. The
 * code belongs to one account, so it is sent with that account's address.
 */
export interface PasswordResetRequest {
  email: string;
  /** From the email, or the link's `?token=`. */
  code: string;
  password: string;
}

/** A new sign-in email, with the current password as proof that the owner is asking. */
export interface EmailChangeRequest {
  newEmail: string;
  password: string;
}

/** One row of a shopper's order history. */
export interface AccountOrderSummary {
  orderNumber: string;
  /** ISO-8601 instant. */
  placedAt?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  fulfilmentStatus?: string | null;
  total?: DecimalString | null;
  currency?: string | null;
  lineCount: number;
}

/** One address in the shopper's address book. `id` only ever names their own. */
export interface AccountAddress {
  id: number;
  /** "Home", "Work", "Mum's". */
  label?: string | null;
  recipientName: string;
  company?: string | null;
  line1: string;
  line2?: string | null;
  locality?: string | null;
  region?: string | null;
  postcode?: string | null;
  /** ISO 3166-1 alpha-2. */
  countryCode: string;
  phone?: string | null;
  /** True on at most one address. */
  defaultAddress: boolean;
}

/** An address to save or change: the checkout's fields plus a label. */
export interface AccountAddressInput {
  label?: string;
  recipientName: string;
  company?: string;
  line1: string;
  line2?: string;
  locality?: string;
  region?: string;
  postcode?: string;
  countryCode: string;
  phone?: string;
  /** Leave unset to keep the current default. */
  makeDefault?: boolean;
}

/**
 * Why an account call was refused, as a code a block can translate.
 *
 * <p>`INVALID_CREDENTIALS` deliberately covers no-such-account, wrong password,
 * locked and staff-account alike: a sign-in form that says which of those it was
 * tells a stranger which addresses shop here.
 */
export type AccountErrorKind =
  | 'INVALID_INPUT'
  | 'EMAIL_TAKEN'
  | 'INVALID_CREDENTIALS'
  | 'NOT_SIGNED_IN'
  | 'INVALID_RESET'
  | 'INVALID_ACTIVATION'
  | 'WEAK_PASSWORD'
  | 'PASSWORD_REUSED'
  | 'REGISTRATION_UNAVAILABLE'
  | 'NOT_FOUND'
  | 'INVALID_LINK'
  | 'RATE_LIMITED'
  | 'UNAVAILABLE';

/** A published review of a product. */
export interface ProductReview {
  id: number;
  /** Null once the reviewer's personal data has been erased; the words stay. */
  displayName?: string | null;
  /** 1–5. */
  rating: number;
  title?: string | null;
  body: string;
  /** Proved by the reviewer's own order line, never by an email they typed. */
  verifiedPurchase: boolean;
  /** ISO-8601 instant. */
  createdAt?: string | null;
  /** The shop's public answer. */
  reply?: string | null;
  repliedAt?: string | null;
}

export interface ProductReviewSummary {
  /** One decimal place, e.g. `"4.3"`; null when there are no reviews — never `"0.0"`. */
  average?: DecimalString | null;
  count: number;
  /** How many reviews gave 1★, 2★ … 5★, in that order. */
  distribution: number[];
}

export interface ProductReviewPage {
  summary: ProductReviewSummary;
  /** Newest first. */
  items: ProductReview[];
  page: number;
  size: number;
  total: number;
}

export interface ReviewSubmission {
  productId: number;
  rating: number;
  title?: string;
  body: string;
  /** Required from a guest; a signed-in shopper who leaves it blank is shown as "Sam S.". */
  displayName?: string;
  /** Required from a guest; a signed-in shopper's review is filed under their account. */
  email?: string;
}

export interface ReviewReceipt {
  /** Always `PENDING`: nothing is published until the shop has read it. */
  status: string;
  verifiedPurchase: boolean;
}

export class AccountError extends Error {
  readonly kind: AccountErrorKind;
  readonly status: number;

  constructor(kind: AccountErrorKind, status: number, message?: string) {
    super(message || kind);
    this.name = 'AccountError';
    this.kind = kind;
    this.status = status;
  }
}

export interface CommerceSource {
  /** Paginated, facetted product listing. Returns an empty `items` with zero
   *  `total` for "no results" — that is not an error and must not throw. */
  listProducts(query?: ProductListingQuery): Promise<ProductListing>;

  /** One product by slug, or `null` when it does not exist — the route calls
   *  `notFound()` on null. A moved slug resolves with `redirectTo` set. */
  getProductBySlug(slug: string): Promise<ProductDetail | null>;

  /** The category tree for navigation and the `/c/**` routes. Empty array
   *  when the tenant has no taxonomy yet. */
  listCategories(): Promise<CategoryNode[]>;

  /** Brands, for a filter. Note the platform ships brands as a facet rather
   *  than as pages: a logo plus a grid is thin content that competes with the
   *  category it duplicates. */
  listBrands(): Promise<BrandCard[]>;

  /**
   * Facets alone, for a filter panel rendered beside a grid rather than inside
   * it. Optional: a host that has not implemented it leaves it undefined, and
   * a panel falls back to `listProducts({ ...query, size: 1 })` and discards
   * the items — correct, but two round trips for one page. Implement it when
   * the backend can answer facets without also assembling product cards.
   */
  listFacets?(query?: ProductListingQuery): Promise<Facet[]>;

  // ── Cart ──────────────────────────────────────────────────────────────────
  // Optional as a group. A quote-led site — one selling £6k kits that close on a
  // phone call — mounts a source with none of these, and the cart blocks then
  // render their "not available" state instead of a broken basket. Implement all
  // of them or none; a half-cart is worse than no cart.

  /** Starts an empty basket. The returned token identifies it from then on. */
  createCart?(): Promise<Cart>;

  /** The basket for a token, or `null` when it has expired or never existed.
   *  Null is normal — baskets are disposable — and must not throw. */
  getCart?(token: string): Promise<Cart | null>;

  addToCart?(token: string, item: CartAddition): Promise<Cart>;

  /** Sets an absolute quantity, not a delta, so a double-submit is harmless. */
  setCartLineQuantity?(token: string, lineId: number, quantity: DecimalString): Promise<Cart>;

  removeCartLine?(token: string, lineId: number): Promise<Cart>;

  applyCoupon?(token: string, code: string): Promise<Cart>;
  removeCoupon?(token: string): Promise<Cart>;

  /**
   * Chooses how the basket is to be fulfilled, and re-prices it.
   *
   * <p>Separate from {@link checkout} so the shopper sees the delivery charge
   * in the total BEFORE they commit, not after. A basket that says "calculated
   * at checkout" and then adds £3.95 on the confirmation screen is the oldest
   * dark pattern in retail, and the only way to avoid it is to let the server
   * re-total the basket the moment a method is picked.
   */
  setCartFulfilment?(token: string, fulfilment: CartFulfilment): Promise<Cart>;

  /** Places the order and consumes the basket. The token is dead afterwards. */
  checkout?(request: CheckoutRequest): Promise<Order>;

  /**
   * One order for a guest, by number plus BOTH proofs.
   *
   * <p>Email and postcode are required together on purpose. An order number is
   * sequential and therefore guessable, and an email alone is often known; the
   * pair is what a stranger does not have. The backend checks both even when the
   * order does not exist, so a wrong guess cannot be told from a missing order.
   * Returns `null` for either — do not report "wrong postcode" to the caller.
   */
  trackOrder?(orderNumber: string, proof: OrderLookup): Promise<Order | null>;

  /**
   * Asks to send items back. A signed-in shopper's own order needs no proof; a guest passes the
   * order's email and postcode, as for {@link trackOrder}.
   *
   * <p>Refusals throw an {@link AccountError}: `INVALID_INPUT` (nothing chosen, more than is left,
   * no reason), `NOT_FOUND` (the order is not proved), `RATE_LIMITED`, `UNAVAILABLE`.
   */
  requestReturn?(input: ReturnRequestInput): Promise<OrderReturn>;

  /**
   * Takes payment for an order that has already been placed.
   *
   * <p>Separate from {@link checkout} because they fail differently and must be retried
   * differently. A checkout that fails leaves nothing behind and can simply be done again; a
   * payment that fails leaves an order that exists, is unpaid, and must not be ordered twice to
   * get another attempt at paying for it.
   *
   * <p>Neither the amount nor the acquirer is passed: the server takes the amount from the order
   * and the provider from the tenant's configuration. A client able to name either could pay a
   * penny for a hundred pounds of goods, or route a live card at a sandbox.
   */
  payForOrder?(request: PaymentRequest): Promise<PaymentResult>;

  /** How this shop takes money. Null when the host cannot say, which a checkout
   *  treats as "no payment step" rather than as an error. */
  paymentOptions?(): Promise<PaymentOptions | null>;

  /**
   * Where a payment got to.
   *
   * <p>For the page a shopper lands on when the acquirer sends them back. The browser usually
   * arrives before the gateway's callback does, so `PENDING` means "ask again shortly" rather
   * than "it failed" — never tell someone their payment did not work on the strength of it.
   */
  paymentStatus?(orderNumber: string, proof: OrderLookup): Promise<PaymentResult | null>;

  // ── Account ───────────────────────────────────────────────────────────────
  // Optional as a group, like the cart. Every method that is refused throws an
  // {@link AccountError}, so a form can say something true without parsing text.
  //
  // The session itself is invisible to the page: the host keeps its token in a
  // cookie script cannot read, and a block only ever learns WHO is signed in,
  // never the credential that proves it.

  /** The signed-in shopper, or `null` when nobody is. Null is normal — never throws for it. */
  accountProfile?(): Promise<AccountProfile | null>;

  /**
   * Creates the account, waiting for its address to be confirmed: a link and a
   * code are emailed, and nobody is signed in yet. Refusals: `EMAIL_TAKEN`,
   * `REGISTRATION_UNAVAILABLE`, `INVALID_INPUT`, `RATE_LIMITED`.
   */
  register?(request: RegisterRequest): Promise<void>;

  /**
   * Confirms a new account with the emailed code, sets its password and signs it
   * in. Resolves with the profile, or `null` when the account is ready but could
   * not be signed in straight away. Refusals: `INVALID_ACTIVATION` (a wrong or
   * used code, or another account's), `WEAK_PASSWORD`, `INVALID_INPUT`,
   * `RATE_LIMITED`.
   */
  activateAccount?(request: AccountActivationRequest): Promise<AccountProfile | null>;

  /** Emails a waiting account its code again. Resolves whether or not there is one. */
  resendActivation?(email: string): Promise<void>;

  signIn?(request: SignInRequest): Promise<AccountProfile>;

  /** Ends the session everywhere it could be replayed, not only in this tab. */
  signOut?(): Promise<void>;

  /** Resolves whether or not the address has an account; see `AccountErrorKind`. */
  forgotPassword?(email: string): Promise<void>;

  /**
   * Sets a new password and ends every session the account had. Refusals:
   * `INVALID_RESET` (a wrong, used or expired code, or another account's),
   * `WEAK_PASSWORD`, `PASSWORD_REUSED`, `INVALID_INPUT`, `RATE_LIMITED`.
   */
  resetPassword?(request: PasswordResetRequest): Promise<void>;

  updateAccountProfile?(profile: { firstName: string; lastName: string }): Promise<AccountProfile>;

  /**
   * Asks to sign in with a different email. Nothing changes yet: the new address
   * is sent a link, and the old one is told. Refusals: `INVALID_CREDENTIALS`
   * (the password), `EMAIL_TAKEN`, `INVALID_INPUT`, `NOT_SIGNED_IN`, `RATE_LIMITED`.
   */
  requestEmailChange?(request: EmailChangeRequest): Promise<void>;

  /**
   * Opens the emailed link. Needs no session; every session the account had ends.
   * Refusals: `INVALID_LINK` (expired, used or replaced), `EMAIL_TAKEN`, `RATE_LIMITED`.
   */
  confirmEmailChange?(token: string): Promise<void>;

  /** Newest first. */
  accountOrders?(page?: number, size?: number): Promise<AccountOrderSummary[]>;

  /** One of the signed-in shopper's own orders, or `null`. */
  accountOrder?(orderNumber: string): Promise<Order | null>;

  /**
   * The shopper's saved addresses, default first.
   *
   * <p>Every change resolves with the whole book, because choosing a default or
   * removing one changes other rows too. Saving an address the book already
   * holds updates that one rather than adding a copy; the first saved becomes
   * the default. Refusals: `NOT_SIGNED_IN`, `INVALID_INPUT` (including a full
   * book), `NOT_FOUND`.
   */
  accountAddresses?(): Promise<AccountAddress[]>;

  saveAccountAddress?(address: AccountAddressInput): Promise<AccountAddress[]>;

  updateAccountAddress?(id: number, address: AccountAddressInput): Promise<AccountAddress[]>;

  removeAccountAddress?(id: number): Promise<AccountAddress[]>;

  makeDefaultAccountAddress?(id: number): Promise<AccountAddress[]>;

  // ── Click & collect ───────────────────────────────────────────────────────

  /** Where this shop offers collection; empty when it offers none, which hides the option. */
  collectionPoints?(): Promise<CollectionPoint[]>;

  // ── Reviews ───────────────────────────────────────────────────────────────

  /** Published reviews for one product, or `null` when the product is not this shop's. */
  productReviews?(
    productId: number,
    page?: number,
    size?: number,
  ): Promise<ProductReviewPage | null>;

  /**
   * Files a review for moderation, as the signed-in shopper when there is one.
   *
   * <p>Refusals throw an {@link AccountError}: `INVALID_INPUT`, `EMAIL_TAKEN` (this person has
   * already reviewed the product), `NOT_FOUND`, `RATE_LIMITED`.
   */
  submitReview?(submission: ReviewSubmission): Promise<ReviewReceipt>;
}

const CommerceSourceContext = createContext<CommerceSource | null>(null);

export function CommerceSourceProvider({
  value,
  children,
}: {
  value: CommerceSource;
  children: ReactNode;
}) {
  return <CommerceSourceContext.Provider value={value}>{children}</CommerceSourceContext.Provider>;
}

/** The host-provided source, or `null` when no provider is mounted. Consuming
 *  blocks render an inline placeholder on `null` and on a thrown method. */
export function useCommerceSource(): CommerceSource | null {
  return useContext(CommerceSourceContext);
}
